import React, { useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { toast } from 'react-toastify';
import { useFindOne, useMethod, useUser, useIsLuminia } from '/imports/hooks';
import { strings } from '/imports/strings';
import { fileHref } from '/imports/api/files';
import { Button } from '/imports/ui/Button';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Icon } from '/imports/ui/Icon';
import { defer, formatCurrency, formatDateTime, formatNumber, randomString } from '/imports/utils';
import { Input } from '/imports/ui/Input';
import { copyUtilityCredentialsLink, toastOnError } from '/imports/ui/utils';
import { css } from '/imports/ui/css';
import { TextArea } from '/imports/ui/TextArea';
import { ImageInput } from '/imports/ui/ImageInput';
import { Modal } from '/imports/ui/Modal';
import { ListMethodCatalog } from '/imports/ui/ListMethodCatalog';
import { Project, validateProjectForQuote } from '/imports/api/projects';
import { modelClass as projectDocumentsClass } from '/imports/api/projectDocuments';
import { ProjectDetailsProps, StorageDetails, SystemDetails } from '/imports/ui/ProjectViewDetails';
import { Orgs } from '/imports/api/orgs';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import {
  generateQuoteProposalPdf, getNumProjectQuotes, ProjectQuote, modelClass as projectQuoteClass, projectQuoteProposalType,
  projectQuoteStatus, projectQuoteType, requestCustomQuote, requestPreliminaryProposal
} from '/imports/api/projectQuotes';

const QuotesSectionStyled = styled.div`
  .flex-wrapper {
    display: flex;
    .data-section {
      flex: 2;
    }
    .image-section {
      flex: 1;
      margin-top: 1rem;
      .image {
        width: 32rem;
        height: 32rem;
        background-position: center;
        background-size: cover;
      }
      .change-image {
        margin-top: 1rem;
      }
    }
  }
`;

/** System/storage details and notes editor plus the project system image. */
export const QuotesSection = (props: ProjectDetailsProps & { save: (_update?: Partial<Project> | undefined, skipValidation?: boolean) => Promise<void | undefined> }) => {
  const { project, update, setUpdate, editing, save, error } = props;
  const _id = project._id;
  return (
    <QuotesSectionStyled>
      <div className="flex-wrapper">
        <div className="data-section">
          <SystemDetails project={project} update={update} setUpdate={setUpdate} editing={editing} error={error} />
          <StorageDetails project={project} update={update} setUpdate={setUpdate} editing={editing} error={error} />
          <div className="notes">
            <div><label>{strings.Notes()}</label></div>
            <div><TextArea id="proj-notes" disabled={!editing} value={update.notes === undefined ? project.notes : update.notes} onChange={(notes) => setUpdate({ ...update, notes })} /></div>
          </div>
        </div>
        <div className="image-section">
          <div className="image-message">{strings.ProjectSystemImageMessage()}</div>
          <div className="image" style={{ backgroundImage: `url("${fileHref(project.image)}")` }} />
          <div className="change-image">
            <Button onClick={() => document.getElementById('image-input')?.click()}>{strings.ChangeImage()}</Button>
            <ImageInput
              className="change-image-input"
              id="image-input"
              onChange={(image) => {
                save({ _id, image }, true);
                defer(() => {
                  const input = document.querySelector<HTMLInputElement>('.change-image-input input[type="file"]');
                  if (input) input.value = null as any;
                });
              }}
              accept="image/*" aspect={1.29}
            />
          </div>
        </div>
      </div>
    </QuotesSectionStyled>
  );
};

const QuotesListStyled = styled.div`
  margin-top: 1rem;
  padding: 2rem 0;
  border-top: ${css.inputBorder};
  .quote-buttons {
    margin-bottom: 1rem;
    display: flex;
    gap: 1rem;
  }
  .list-wrapper {
    width: 100%;
    max-height: 25rem;
    display: flex;
  }
  .catalog {
    width: 100%;
    height: 100%;
    max-height: 25rem;
    height: 25rem;
  }
  .actions {
    display: flex;
    gap: 0.25rem;
  }
  .row-select {
    display: none;
  }
  tr.active {
    background-color: ${css.primaryColor};
    color: ${css.white};
    svg {
      fill: ${css.white};
    }
  }
  .status-column {
    .icon {
      position: relative;
      left: 0.2rem;
      top: 0.3rem;
      svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }
`;

/** Catalog of a project's quotes with create/request/edit/delete actions. */
export const QuotesList = (props: { project: Project }) => {
  const { project } = props;
  const projectId = project._id;
  const user = useUser();
  const isLuminia = useIsLuminia();
  const orgId = user?.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const isDealer = org.projectChannel === 'dealer';
  const [showCreateQuoteModal, setShowCreateQuoteModal] = useState('');
  const [showRequestQuoteModal, setShowRequestQuoteModal] = useState(false);
  const [showRequestProposalModal, setShowRequestProposalModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState<ProjectQuote>();
  const [editingQuote, setEditingQuote] = useState<ProjectQuote>();
  const [deletingQuote, setDeletingQuote] = useState<ProjectQuote>();
  const [updateQuotesList, setUpdateQuotesList] = useState(randomString());
  const [numQuotes] = useMethod(getNumProjectQuotes, { projectId }, [updateQuotesList]);
  /**
   * Generate a proposal PDF for a quote, blocking non-superusers on negative savings.
   * @param quote - quote to render
   */
  const generateQuotePdf = toastOnError(async (quote: ProjectQuote) => {
    const negativeSavings = !quote.modelResult?.yearOneSavings || quote.modelResult?.yearOneSavings < 0;
    if (!isLuminia && negativeSavings) return toast.error(strings.QuoteParametersGenerateNegiveSavingsMessage());
    const quoteId = quote._id;
    const toastId = toast.info(strings.GeneratingProposal(), { autoClose: 10000 });
    await generateQuoteProposalPdf.call({ quoteId });
    toast.dismiss(toastId);
    toast.success(strings.Done());
    setUpdateQuotesList(randomString());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport());
  /**
   * Mark a quote as the active proposal.
   * @param _id - quote id
   */
  const setAsActive = toastOnError(async (_id: string) => {
    await projectQuoteClass.updateMethod.call({ _id, status: 'active' });
    setUpdateQuotesList(randomString());
  });
  /**
   * Re-save a quote to bump it to the top of the list.
   * @param quote - quote to move
   */
  const moveToTop = toastOnError(async (quote: ProjectQuote) => {
    await projectQuoteClass.updateMethod.call({ _id: quote._id, name: quote.name });
    setUpdateQuotesList(randomString());
  });
  /**
   * Delete a quote and refresh the list.
   * @param quote - quote to delete
   */
  const deleteQuote = toastOnError(async (quote: ProjectQuote) => {
    await projectQuoteClass.deleteMethod.call(quote._id);
    toast.success(strings.Deleted());
    setDeletingQuote(undefined);
    setUpdateQuotesList(randomString());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => setDeletingQuote(undefined));
  /**
   * Whether a quote can be generated; for dealers, prompts to set EPC prices first.
   * @returns true when generation may proceed
   */
  const canGenerateQuote = () => {
    if (!isDealer) return true;
    if (!project.epcPrice || !project.essEpcPrice) {
      toast.error(strings.PleaseEditThisProjectToUpdateTheEpcPrice());
      document.getElementById('edit-button')?.click();
      setTimeout(() => document.getElementById('save-btn')?.click(), 250);
    }
    else return true;
  };
  const Catalog = useMemo(() => (
    <ListMethodCatalog
      key={updateQuotesList}
      data={[]}
      columns={[
        { field: 'name', width: '15rem' },
        {
          field: 'actions', label: strings.Actions(), width: '12rem', render: (q) => {
            return (
              <span className='actions'>
                <a href="#"><Icon icon="info" onClick={() => setShowDetailsModal(q)} tip={strings.ViewDetails()} /></a>
                {!q.pdf?.url ? <a href="#"><Icon icon="publish" onClick={() => generateQuotePdf(q)} tip={strings.GenerateProposal()} /></a> : null}
                {q.pdf?.url ? <a href={fileHref(q.pdf)} target="_blank" rel="noreferrer"><Icon icon="documentSearch" tip={strings.ViewProposalPdf()} /></a> : null}
                {q.pdf?.url && q.status !== 'active' ? <a href="#"><Icon icon="modeOffOn" onClick={() => setAsActive(q._id)} tip={strings.SetAsActiveProposal()} /></a> : null}
                {
                  q.status === 'active' && ['preliminaryProposal', 'siteSurvey', 'detailedProposal'].includes(project.status as string) ?
                    <a href="#"><Icon icon="documentScanner" onClick={() => setShowRequestProposalModal(true)} tip={strings.RequestPreliminaryDetailedProposal()} /></a> : null
                }
                <a href="#"><Icon icon="keyboardDoubleArrowUp" onClick={() => moveToTop(q)} tip={strings.MoveToTop()} /></a>
                <a href="#"><Icon icon="edit" onClick={() => setEditingQuote(q)} tip={strings.Edit()} /></a>
                <a href="#"><Icon icon="close" onClick={() => setDeletingQuote(q)} tip={strings.Delete()} /></a>
              </span>
            );
          }
        },
        { field: 'proposalType', render: (q) => projectQuoteProposalType(q) },
        { field: 'quoteType', render: (q) => projectQuoteType(q) },
        {
          field: 'status',
          render: (q) => projectQuoteStatus(q),
          label: () => <span className="status-column">{strings.Status()}<Icon icon="info" tip={<span dangerouslySetInnerHTML={{ __html: strings.QuoteStatusTip() }} />} /></span>
        },
        { field: 'createdAt' },
        { field: 'systemSize', label: `${strings.SolarPvSystemSize()} (kW DC)`, render: (q) => formatNumber(q.modelResult?.solarSystemSize) },
        { field: 'essCapacity', label: `${strings.EssCapacity()} (kWh)`, render: (q) => formatNumber(q.modelResult?.essEnergyCapcity) },
        { field: 'yearOneSavings', label: strings.YearOneSavings(), render: (q) => formatCurrency(q.modelResult?.yearOneSavings || 0, 0) },
        { field: 'termSavings', label: strings.TermSavings(), render: (q) => formatCurrency(q.modelResult?.totalCeaAnnualSavings || 0, 0) },
      ]}
      rowClass={(q) => q.status}
      query={{ active: true, projectId }}
      defaultSort={{ updatedAt: -1 }}
      listMethod={projectQuoteClass.listMethod}
    />
  ), [updateQuotesList]);
  return (
    <QuotesListStyled>
      <div className="quote-buttons">
        <Button id="gen-quote-btn" onClick={() => { if (canGenerateQuote()) setShowCreateQuoteModal('cea'); }} tip={strings.GenerateNewQuote()}>{strings.GenerateQuote()}</Button>
        <Button id="req-custom-btn" onClick={() => { setShowRequestQuoteModal(true); }}>{strings.RequestCustomProposal()}</Button>
        <Button id="make-credentials-link" onClick={() => copyUtilityCredentialsLink(project._id)}>{strings.GenerateUtilityDataLink()}</Button>
      </div>
      <div className="list-wrapper">
        {Catalog}
      </div>
      {
        showCreateQuoteModal === 'cea' ?
          <CeaCreateQuoteModal project={project} defaultName={`${strings.Proposal()} ${(numQuotes || 0) + 1}`}
            onDone={() => { setShowCreateQuoteModal(''); setUpdateQuotesList(randomString()); }}
            onClose={() => setShowCreateQuoteModal('')}
          />
          : null
      }
      {
        showRequestQuoteModal ?
          <RequestQuoteModal projectId={projectId}
            onRequest={() => { setShowRequestQuoteModal(false); }}
            onClose={() => setShowRequestQuoteModal(false)}
          />
          : null
      }
      {
        showRequestProposalModal ?
          <RequestProposalModal projectId={projectId}
            onRequest={() => { setShowRequestProposalModal(false); }}
            onClose={() => setShowRequestProposalModal(false)}
          />
          : null
      }
      {
        showDetailsModal ?
          <CeaQuoteDetailsModal quote={showDetailsModal} onClose={() => setShowDetailsModal(undefined)} /> : null
      }
      {
        editingQuote ?
          <EditQuoteModal
            quote={editingQuote}
            onClose={() => setEditingQuote(undefined)}
            onSaved={() => { setEditingQuote(undefined); setUpdateQuotesList(randomString()); }}
          /> : null
      }
      {
        deletingQuote ?
          <ConfirmModal
            title={strings.Delete()}
            body={<div>{strings.AreYouSureYouWantToDeleteTheQuote()}</div>}
            onClose={() => setDeletingQuote(undefined)}
            onConfirm={() => deleteQuote(deletingQuote)}
            confirmText={strings.Delete()}
          /> : null
      }
    </QuotesListStyled>
  );
};

const RequestQuoteModalStyled = styled(Modal)`
  .buttons {
    margin: 1.5rem 0;
    display: flex;
    gap: 1rem;
  }
  .modal-body {
    position: relative;
  }
`;

interface RequestQuoteModalProps {
  projectId: string;
  onRequest?: (proposalType: ProjectQuote['proposalType']) => void;
  onClose?: () => void;
}

/** Modal for requesting a custom proposal, confirming the chosen proposal type. */
const RequestQuoteModal = (props: RequestQuoteModalProps) => {
  const { projectId, onRequest, onClose } = props;
  const [requesting, setRequesting] = useState(false);
  const [quoteType, setQuoteType] = useState<ProjectQuote['proposalType']>();
  /**
   * Submit a custom-quote request.
   * @param proposalType - requested proposal type
   */
  const requestQuote = toastOnError(async (proposalType: ProjectQuote['proposalType']) => {
    setRequesting(true);
    await requestCustomQuote.call({ projectId, proposalType });
    onRequest?.(proposalType);
    setRequesting(false);
    setQuoteType(undefined);
    toast.success(strings.YouHaveRequestedCustomProposalMessage());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => onClose?.());
  return (
    <>
      {
        quoteType ?
          <ConfirmModal
            title={strings.RequestCustomProposal()}
            onClose={() => { setQuoteType(undefined); onClose?.(); }}
            body={(<div>{strings.AreYouSureYouWouldLikeToRequestCustomProposal()}</div>)}
            onConfirm={() => requestQuote(quoteType)}
            confirmText={strings.Yes()}
            rejectText={strings.No()}
          />
          :
          <RequestQuoteModalStyled
            title={strings.RequestCustomProposal()}
            onClose={() => onClose?.()}
            body={
              <>
                <p>{strings.WhatKindOfProposalIsTheCustomerLookingFor()}</p>
                <div className="buttons">
                  <Button onClick={() => setQuoteType('essSolar')}>{strings.SolarPlusEnergyStorage()}</Button>
                  <Button>{strings.EnergyStorageOnly()}</Button>
                </div>
                {requesting ? <LoadingFlex /> : null}
              </>
            }
          />
      }
    </>
  );
};

const RequestProposalModalStyled = styled(Modal)`
  .buttons {
    margin: 1.5rem 0;
    display: flex;
    gap: 1rem;
    justify-content: center;
  }
  .modal-body {
    position: relative;
  }
`;

interface RequestProposalModalProps {
  projectId: string;
  onRequest?: () => void;
  onClose?: () => void;
}

/** Modal confirming a request for a preliminary/detailed proposal. */
const RequestProposalModal = (props: RequestProposalModalProps) => {
  const { projectId, onRequest, onClose } = props;
  const [requesting, setRequesting] = useState(false);
  /** Submit the preliminary-proposal request. */
  const requestProposal = toastOnError(async () => {
    setRequesting(true);
    await requestPreliminaryProposal.call({ projectId });
    onRequest?.();
    setRequesting(false);
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => onClose?.());
  return (
    <RequestProposalModalStyled
      title={strings.RequestPreliminaryDetailedProposal()}
      onClose={() => onClose?.()}
      body={
        <>
          <p>{strings.AreYouSureYouWantToRequestPreliminaryDetailedProposal()}</p>
          <div className="buttons">
            <Button onClick={() => requestProposal()}>{strings.Yes()}</Button>
            <Button onClick={() => onClose?.()}>{strings.No()}</Button>
          </div>
          {requesting ? <LoadingFlex /> : null}
        </>
      }
    />
  );
};

const EditQuoteModalStyled = styled(Modal)`
  input {
    width: 100%;
  }
  .buttons {
    margin: 1.5rem 0;
    display: flex;
    gap: 1rem;
  }
`;

interface EditQuoteModalProps {
  quote: ProjectQuote;
  onClose: () => void;
  onSaved: () => void;
}

/** Modal for renaming a quote. */
const EditQuoteModal = (props: EditQuoteModalProps) => {
  const { quote, onClose, onSaved } = props;
  const [name, setName] = useState<string | undefined>(quote.name);
  const [saving, setSaving] = useState(false);
  /** Persist the edited quote name. */
  const save = toastOnError(async () => {
    if (!name?.trim()) return;
    setSaving(true);
    await projectQuoteClass.updateMethod.call({ _id: quote._id, name });
    toast.success(strings.Saved());
    setSaving(false);
    onSaved();
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => setSaving(false));
  return (
    <EditQuoteModalStyled
      title={strings.Edit()}
      onClose={onClose}
      body={
        <>
          <div>
            <div><label>{strings.Name()}</label></div>
            <div><Input id="edit-quote-name" value={name} onChange={(name) => setName(name)} /></div>
          </div>
          <div className="buttons">
            <Button id="edit-quote-save" className="primary" onClick={() => save()} disabled={saving || !name?.trim()}>{strings.Save()}</Button>
            <Button id="edit-quote-cancel" className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
          </div>
        </>
      }
    />
  );
};

const CeaCreateQuoteModalStyled = styled(Modal)`
  input {
    width: 100%;
  }
  .buttons {
    margin: 1.5rem 0;
    display: flex;
    gap: 1rem;
    .btn {
      width: 18rem;
    }
  }
  .modal-body {
    position: relative;
  }
  .loading-flex {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.5;
  }
`;

interface CeaCreateQuoteModalProps {
  project: Project;
  defaultName: string;
  onGenerateQuote?: () => void;
  onDone?: () => void;
  onClose?: () => void;
}

/** Modal for generating a CEA quote (indicative or detailed, solar+ESS or ESS-only). */
const CeaCreateQuoteModal = (props: CeaCreateQuoteModalProps) => {
  const { project, defaultName, onGenerateQuote, onDone, onClose } = props;
  const projectId = project._id;
  const isLuminia = useIsLuminia();
  const [name, setName] = useState<string | undefined>(defaultName);
  const [projectDocuments, loadingDocuments] = useMethod(projectDocumentsClass.listMethod, { query: { projectId, type: 'intervalData' } }, [projectId]);
  const [generating, setGenerating] = useState(false);
  /**
   * Validate the project then create the CEA quote.
   * @param quoteType - indicative or detailed
   * @param proposalType - solar+ESS or ESS-only
   */
  const generateCeaQuote = toastOnError(async (quoteType: ProjectQuote['quoteType'], proposalType: ProjectQuote['proposalType']) => {
    setGenerating(true);
    const toastOpts = { autoClose: 10000, style: { width: '35rem' } };
    const errors = isLuminia ? [] : await validateProjectForQuote.call({ projectId, proposalType });
    if (errors.length) {
      toast.error(
        <div>
          {errors.map((e) => <div key={e} style={{ marginBottom: '0.5rem' }}>{e}</div>)}
          <div>{strings.PleaseRequestCustomProposalMessage()}</div>
        </div>
        , toastOpts);
      setGenerating(false);
      return;
    }
    onGenerateQuote?.();
    const toastId = toast.info(strings.GeneratingQuote(), { autoClose: 10000 });
    await projectQuoteClass.createMethod.call({ projectId, name, quoteType, proposalType });
    toast.dismiss(toastId);
    setGenerating(false);
    onDone?.();
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => onClose?.());
  const hasConsumptionData = projectDocuments?.documents.length;
  return (
    <CeaCreateQuoteModalStyled
      title={strings.GenerateQuote()}
      onClose={() => onClose?.()}
      body={
        <>
          <div>
            <div><label>{strings.Name()}</label></div>
            <div><Input id="quote-name" value={name} onChange={(name) => setName(name)} /></div>
          </div>
          <p>{strings.WhatKindOfProposalIsTheCustomerLookingFor()}</p>
          <div className="buttons">
            <Button id="gen-indicative" onClick={() => generateCeaQuote('ceaIndicative', 'essSolar')}>
              {strings.SolarPlusEnergyStorage()} ({strings.Indicative()})
            </Button>
            <Button id="gen-ess-only" onClick={() => generateCeaQuote('ceaIndicative', 'ess')}>
              {strings.EnergyStorageOnly()} ({strings.Indicative()})
            </Button>
          </div>
          {
            isLuminia ?
              <div className="buttons">
                <Button id="gen-detailed" disabled={!hasConsumptionData} onClick={() => generateCeaQuote('ceaDetailed', 'essSolar')} tip={strings.YouMustUploadConsumptionDataIntheDataroomToGenerateDetailedProposals()}>
                  {strings.SolarPlusEnergyStorage()} ({strings.Detailed()})
                </Button>
                <Button id="gen-detailed-ess" disabled={!hasConsumptionData} onClick={() => generateCeaQuote('ceaDetailed', 'ess')} tip={strings.YouMustUploadConsumptionDataIntheDataroomToGenerateDetailedProposals()}>
                  {strings.EnergyStorageOnly()} ({strings.Detailed()})
                </Button>
              </div>
              :
              null
          }
          {generating || loadingDocuments ? <LoadingFlex /> : null}
        </>
      }
    />
  );
};

const CeaQuoteDetailsModalStyled = styled(Modal)`
  .modal-window {
    height: 100%;
    max-height: 46.75rem;
    overflow: hidden;
  }
  .modal-body {
    height: calc(100% - 2.875rem);
    overflow: auto;
  }
  table {
    tbody {
      tr {
        &:nth-child(odd) {
          background-color: ${css.grey8};
        }
      }
      td {
        padding: 0.5rem 1rem;
        outline: 1px solid ${css.grey6};
      }
    }
  }
`;

interface CeaQuoteDetailsModalProps {
  quote: ProjectQuote;
  onClose?: () => void;
}

/** Read-only modal showing a CEA quote's inputs and modeled results. */
const CeaQuoteDetailsModal = (props: CeaQuoteDetailsModalProps) => {
  const { quote, onClose } = props;
  const term = quote.proposalType === 'essSolar' ? 25 : 10;
  return (
    <CeaQuoteDetailsModalStyled
      title={strings.ViewDetails()}
      onClose={() => onClose?.()}
      body={
        <table>
          <tbody>
            <tr><td>{strings.Name()}</td><td>{quote.name}</td></tr>
            <tr><td>{strings.ProposalType()}</td><td>{projectQuoteProposalType(quote)}</td></tr>
            <tr><td>{strings.QuoteType()}</td><td>{projectQuoteType(quote)}</td></tr>
            <tr><td>{strings.CreatedAt()}</td><td>{formatDateTime(quote.createdAt)}</td></tr>
            <tr><td>{strings.Status()}</td><td>{projectQuoteStatus(quote)}</td></tr>
            <tr><td>{strings.SolarPvSystemSize()} (kW DC)</td><td>{formatNumber(quote.modelResult?.solarSystemSize)}</td></tr>
            <tr><td>{strings.EstimatedYearOneProduction()} (kWh)</td><td>{formatNumber(quote.modelResult?.yearOneProduction)}</td></tr>
            <tr><td>{strings.EssCapacity()} (kWh)</td><td>{formatNumber(quote.modelResult?.essEnergyCapcity)}</td></tr>
            <tr><td>{strings.EssSize()} (kW)</td><td>{formatNumber(quote.modelResult?.essPowerCapacity)}</td></tr>
            <tr><td>{strings.EpcPrice()} ($/W)</td><td>{formatNumber(quote.projectInputs?.epcPrice, 2)}</td></tr>
            <tr><td>{strings.EssEpcPrice()} ($/kWh)</td><td>{formatNumber(quote.projectInputs?.essEpcPrice, 2)}</td></tr>
            <tr><td>{strings.AvoidedCost()} ($/kWh)</td><td>{formatNumber(quote.projectInputs?.avoidedCost, 3)}</td></tr>
            <tr><td>{strings.EssAvoidedCost()} ($/kWh)</td><td>{formatNumber(quote.projectInputs?.essAvoidedCost, 3)}</td></tr>
            <tr><td>{strings.Term()} ({strings.Years()})</td><td>{term}</td></tr>
            {/* Index the schedule defensively: most quotes carry no modelResult at all, and a bare
                schedule[0][5] threw, taking the whole modal down. formatNumber renders '-' for undefined. */}
            <tr><td>{strings.YearOneSavings()}</td><td>${formatNumber(quote.modelResult?.schedule?.[0]?.[5])}</td></tr>
            <tr><td>{strings.YearOneLeaseIncome()}</td><td>${formatNumber(quote.modelResult?.schedule?.[0]?.[6])}</td></tr>
            <tr><td>{strings.TermSavings()}</td><td>${formatNumber(quote.modelResult?.schedule?.[term]?.[5])}</td></tr>
            <tr><td>{strings.TermLeaseIncome()}</td><td>${formatNumber(quote.modelResult?.schedule?.[term]?.[6])}</td></tr>
            <tr><td>{strings.SolarPlusBusinessPortion()}</td><td>{formatNumber(quote.modelResult?.solarPlusBusinessPortion || 0)}%</td></tr>
            <tr><td>{strings.UtilityPortion()}</td><td>{formatNumber(quote.modelResult?.utilityPortion || 0)}%</td></tr>
          </tbody>
        </table>
      }
    />
  );
};

